import { isAnyOf } from '@reduxjs/toolkit';
import { select, take } from 'typed-redux-saga';
import { RobotCleaningControlActions } from '../robotCleaningControlSlice';
import {
  selectRobotCleaningControlIsBeepOn,
  selectRobotCleaningControlIsBlinkOn,
  selectRobotCleaningControlIsPaused,
} from '../robotCleaningControlSelectors';
import type { LocateCommandType } from '../types/RobotCleaningControl.types';
import type { SendCleaningCommandRequestAction } from '../robotCleaningControlActions.types';
import { RobotCleaningProgressActions } from '../../robotCleaningProgress/robotCleaningProgressSlice';
import { selectRobotCleaningProgressEntry } from '../../robotCleaningProgress/robotCleaningProgressSelectors';
import { LocateStatus } from './constants';
import { isCommandConfirmed } from './commandConfirmation';
import { Cv50Commands } from 'app/cross-cutting-concerns/communication/interfaces/pra-api-graphql';

// What can change whether a queued command has taken effect: the task's data or the device's paused state.
const isStateUpdateAction = isAnyOf(
  RobotCleaningProgressActions.openRobotCleaningProgressSuccess,
  RobotCleaningProgressActions.robotCleaningProgressUpdated,
  RobotCleaningProgressActions.robotCleaningTaskUpdated,
  RobotCleaningControlActions.pausedStateReceived
);

// Resolves true once the robot's reported state shows the command's effect. The mutation only queues the
// command, so this is what keeps the control loading until the robot has actually paused, resumed, stopped...
export function* waitUntilCommandTakesEffect(
  payload: Pick<SendCleaningCommandRequestAction['payload'], 'commandType' | 'model' | 'robotId'>,
  baseline: ReturnType<typeof selectRobotCleaningProgressEntry>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Generator<unknown, boolean, any> {
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const task = yield* select(selectRobotCleaningProgressEntry, payload.robotId);
    const isPaused = yield* select(selectRobotCleaningControlIsPaused);

    if (isCommandConfirmed({ commandType: payload.commandType, model: payload.model, baseline, task, isPaused })) {
      return true;
    }

    yield* take(isStateUpdateAction);
  }
}

// Resolves true once the device reports Beep / Blink in the state the command asked for. Like a cleaning command,
// the mutation only queues it, and a press asks for the opposite of the reported state, so the toggle has to stay
// loading until the device has reported the new one.
export function* waitUntilLocateSettingTakesEffect(
  commandType: LocateCommandType,
  status: LocateStatus
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Generator<unknown, boolean, any> {
  const selectIsOn =
    commandType === Cv50Commands.Beep ? selectRobotCleaningControlIsBeepOn : selectRobotCleaningControlIsBlinkOn;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const isOn = yield* select(selectIsOn);

    if (isOn === (status === LocateStatus.On)) {
      return true;
    }

    yield* take(RobotCleaningControlActions.locateSettingsReceived);
  }
}
